import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grow,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { ReasonType } from "@rooster/types";
import { range } from "lodash";
import Lottie from "lottie-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next"; // Added import for translation
import schedulingLottie from "../assets/animations/scheduling.json";
import schedulingErrorLottie from "../assets/animations/schedulingError.json";
import schedulingSuccessLottie from "../assets/animations/schedulingSuccess.json";
import { CancelInterviewDialog } from "../components/Dialogs/CancelInterviewDialog";
import { MeetingWhen } from "../components/Meeting/MeetingWhen";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { initializeSchedulerStore } from "../helpers/init";
import { getLinkType } from "../helpers/link";
import { getAutobookInterviewStatusHelper } from "../helpers/meeting/validate";
import { useAvailability } from "../hooks/useAvailability";
import { useCandidateReasons } from "../hooks/useCandidateReasons";
import { useScreenSize } from "../hooks/useScreenSize";
import { getRoosterAutobookAvailabilityData } from "../services/api/meeting.api";
import { useSchedulerStore } from "../state/scheduler.store";
import { SCHEDULE_STATE } from "../types";
import {
  handleRoosterAutobookAvailabilityGenericErrorMessageState,
  notifySenderOnNoAvailability,
  setNoAvailabilityError
} from "../utils/errorManagement";

interface IMeetingStatusProps {
  status: SCHEDULE_STATE | null;
  currentlySelectedSlot: string | null;
  previouslySelectedTime: string | null;
  previouslySelectedDuration?: number;
  handleCancel: (
    id: string,
    reason?: ReasonType,
    comment?: string
  ) => Promise<void>;
  handleReschedule: (forceInit: boolean) => Promise<void>;
}

export const Meeting: React.FC<IMeetingStatusProps> = ({
  status,
  currentlySelectedSlot,
  previouslySelectedTime,
  previouslySelectedDuration,
  handleCancel,
  handleReschedule
}) => {
  const {
    ui,
    link,
    sessionData,
    meetingLink,
    cleanSession,
    host,
    actions,
    loadingAvailability,
    noAvailabilityLog
  } = useSchedulerStore();
  const { fetchAvailability } = useAvailability();
  const { t } = useTranslation(); // Added translation hook
  const [autobookStatus, setAutobookStatus] = useState<boolean | null>(null);
  const isRoosterLink = useMemo(() => {
    return meetingLink?.type === "rooster_links";
  }, [meetingLink?.type]);

  const [isPreparingSchedulingState, setIsPreparingSchedulingState] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [showCancelDialogState, setShowCancelDialogState] = useState(false);

  const { isSmallScreen, isMediumScreen } = useScreenSize();

  // Mirror the availability id resolved at cancel time (handleCancelDialog) so
  // the reasons lookup is scoped to the same availability the candidate is
  // acting on. The server derives the tenant client from this id.
  // `getLinkType()` is read at render (it parses the URL) and tracked as a
  // dependency, rather than called inside the memo where it would be invisible
  // to the dependency array.
  const parsedLink = getLinkType();
  const cancelAvailabilityId = useMemo(() => {
    if (meetingLink?.type === "rooster_links") {
      return parsedLink?.availabilityId ?? null;
    }
    if (meetingLink?.type === "rooster") {
      return link?.scheduleId ?? null;
    }
    return null;
  }, [meetingLink?.type, link?.scheduleId, parsedLink?.availabilityId]);

  const {
    reasons: cancelReasons,
    isRequired: isCancelRequired,
    showComment: showCancelComment,
    loading: cancelReasonsLoading
  } = useCandidateReasons({
    availabilityId: cancelAvailabilityId,
    operation: "cancel",
    // Reasons apply to autobook interviews only — never RoosterLinks/prospect.
    enabled: showCancelDialogState && meetingLink?.type === "rooster"
  });

  const [cancelledReason, setCancelledReason] = useState<{
    reason?: string;
    comment?: string;
  } | null>(null);

  const handleCancelDialog = async (reason?: ReasonType, comment?: string) => {
    let scheduleId: string | null = null;

    if (meetingLink?.type === "rooster_links") {
      scheduleId = getLinkType()?.availabilityId ?? null;
    } else if (meetingLink?.type === "rooster") {
      scheduleId = link?.scheduleId ?? null;
    }

    if (scheduleId) {
      setIsCancelling(true);
      setShowCancelDialogState(false);
      try {
        await handleCancel(scheduleId, reason, comment);
        setCancelledReason({ reason: reason?.reason, comment });
      } finally {
        setIsCancelling(false);
      }
    }
  };

  const handleScheduleAgain = async () => {
    await initializeSchedulerStore(actions, t);
    actions.setUiState({
      currentStep: "initial",
      currentView: "month"
    });
    await fetchAvailability();
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialogState(false);
  };

  const handleRescheduleClick = async (forceInit?: boolean) => {
    if (
      sessionData?.computedAvailability?.length === 0 &&
      link?.scheduleId &&
      noAvailabilityLog
    ) {
      await notifySenderOnNoAvailability(
        noAvailabilityLog,
        link?.scheduleId,
        sessionData
      );
      setNoAvailabilityError(actions, t);
      return;
    }

    // Validate reschedule parameters before proceeding
    if (link?.scheduleId && meetingLink?.type === "rooster") {
      const autobookAvailabilityData = await getRoosterAutobookAvailabilityData(
        {
          linkId: link?.scheduleId ?? ""
        }
      );

      // Check if reschedule is allowed
      if (
        autobookAvailabilityData &&
        "docData" in autobookAvailabilityData &&
        autobookAvailabilityData.docData?.reschedule === false
      ) {
        // Update the store with the latest reschedule status
        actions.setMeetingLink({
          ...meetingLink,
          canReschedule: false,
          rescheduleStatus:
            autobookAvailabilityData.docData?.rescheduleStatus ?? null
        });

        // Refresh the page to show updated state with disabled reschedule button
        window.location.reload();
        return;
      }
    }

    const parsedUrlSegments = getLinkType();
    if (
      meetingLink?.type === "rooster_links"
        ? parsedUrlSegments?.availabilityId
        : parsedUrlSegments?.linkId
    ) {
      setIsPreparingSchedulingState(true);
      await handleReschedule(!cleanSession);
      setIsPreparingSchedulingState(false);
    }
  };

  const autobookCancelOrRescheduleAlertMessage = useMemo(() => {
    if (
      !meetingLink?.canReschedule &&
      meetingLink?.rescheduleStatus === "cutoff-window-violation"
    ) {
      return t("ROOSTER.PUBLIC-APP.RESCHEDULE_CUTOFF_VIOLATION", {
        defaultValue:
          "You can no longer reschedule your interview. Please reach out to your recruiter directly."
      });
    }

    if (
      !meetingLink?.canReschedule &&
      meetingLink?.rescheduleStatus === "max-reschedule-violation"
    ) {
      return t("ROOSTER.PUBLIC-APP.MAX_RESCHEDULES_VIOLATION", {
        defaultValue:
          "You’ve reached the maximum number of reschedules allowed for this interview. Please reach out to your recruiter directly."
      });
    }

    if (!meetingLink?.canCancel && meetingLink?.canReschedule) {
      return t("ROOSTER.PUBLIC-APP.CANCEL_INTERVIEW_CONTACT_RECRUITER", {
        defaultValue:
          "If you need to cancel this interview please contact the recruiter"
      });
    }

    if (!meetingLink?.canReschedule && meetingLink?.canCancel) {
      return t("ROOSTER.PUBLIC-APP.RESCHEDULE_INTERVIEW_CONTACT_RECRUITER", {
        defaultValue:
          "If you need to reschedule this interview please contact the recruiter"
      });
    }

    if (!meetingLink?.canCancel || !meetingLink?.canReschedule) {
      return t(
        "ROOSTER.PUBLIC-APP.RESCHEDULE_CANCEL_INTERVIEW_CONTACT_RECRUITER",
        {
          defaultValue:
            "If you need to reschedule or cancel this interview please contact the recruiter"
        }
      );
    }
  }, [meetingLink, t]);

  const roosterLinkAvailabilityMessage = useMemo(() => {
    if (!meetingLink?.canCancel && !meetingLink?.canReschedule) {
      return t("ROOSTER.PUBLIC-APP.NO_RESCHEDULE_NO_CANCEL_MEETING", {
        defaultValue:
          "Reschedule or Cancellation is not available for this meeting."
      });
    }

    if (!meetingLink?.canCancel) {
      return t("ROOSTER.PUBLIC-APP.NO_CANCEL_MEETING", {
        defaultValue: "Cancellation is not available for this meeting."
      });
    }

    return t("ROOSTER.PUBLIC-APP.NO_RESCHEDULE_MEETING", {
      defaultValue: "Reschedule is not available for this meeting."
    });
  }, [meetingLink, t]);

  useEffect(() => {
    if (
      (meetingLink?.type === "rooster" && meetingLink?.mode === "autobook") ||
      meetingLink?.type === "rooster_links"
    ) {
      const checkAutobookStatusWithRetry = async () => {
        const maxRetries = 10;
        const baseDelay = 1000; // 1 second
        let retryCount = 0;

        const retry = async () => {
          try {
            const result = await getAutobookInterviewStatusHelper({
              link
            });
            const isSent = result?.isSentState ?? false;
            const failed = result?.failed ?? false;

            if (failed) {
              actions.setUiState({
                currentStep: "scheduling_failed",
                currentView: "month"
              });
              handleRoosterAutobookAvailabilityGenericErrorMessageState(
                actions,
                {
                  code: "MEETING_SCHEDULING_FAILED",
                  initiatorEmail: host?.email,
                  initiatorName: host?.name
                },
                t
              );
              return;
            }

            setAutobookStatus(isSent);

            if (isSent || retryCount >= maxRetries) {
              return;
            }

            retryCount++;
            const delay = baseDelay * 2 ** (retryCount - 1);
            setTimeout(retry, delay);
          } catch (error) {
            console.error("Error checking autobook status:", error);
            if (retryCount < maxRetries) {
              retryCount++;
              const delay = baseDelay * 2 ** (retryCount - 1);
              setTimeout(retry, delay);
            }
          }
        };

        await retry();
      };

      checkAutobookStatusWithRetry();
    }
  }, [meetingLink]);

  return (
    <Grow in={true} style={{ transformOrigin: "1 2 2" }} timeout={150}>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100dvh" }}
        flex={1}
        data-testid="meeting-page-container"
      >
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          alignItems="flex-start"
        >
          <Stack direction="column">
            <Card
              variant="outlined"
              raised={false}
              sx={{ borderWidth: { xs: "0px", md: "1px" } }}
              data-testid="meeting-status-card"
            >
              <Stack
                direction="column"
                flex={1}
                alignItems="center"
                sx={{ px: 3, width: { sm: "100vw", md: "520px" } }}
              >
                {ui?.currentStep !== "loading" && (
                  <>
                    <CancelInterviewDialog
                      show={showCancelDialogState}
                      onCancel={handleCancelDialog}
                      onClose={handleCloseCancelDialog}
                      reasons={cancelReasons}
                      isRequired={isCancelRequired}
                      showComment={showCancelComment}
                      loading={cancelReasonsLoading}
                    />
                  </>
                )}
                <Stack sx={{ pt: 1 }}>
                  {ui?.currentStep === "loading" ? (
                    <Skeleton
                      variant="circular"
                      height="150px"
                      width="150px"
                      sx={{ mt: 1, mb: 1 }}
                    />
                  ) : (
                    <Lottie
                      animationData={
                        status === SCHEDULE_STATE.SCHEDULING
                          ? schedulingLottie
                          : status === SCHEDULE_STATE.SCHEDULING_SUCCESS
                            ? schedulingSuccessLottie
                            : schedulingErrorLottie
                      }
                      style={{
                        height:
                          status === SCHEDULE_STATE.SCHEDULING
                            ? "180px"
                            : "150px"
                      }}
                      loop={status === SCHEDULE_STATE.SCHEDULING}
                      role="img"
                      aria-label={
                        status === SCHEDULE_STATE.SCHEDULING
                          ? t("ROOSTER.PUBLIC-APP.SCHEDULING_ANIMATION_ALT", {
                              defaultValue: "Scheduling in progress"
                            })
                          : status === SCHEDULE_STATE.SCHEDULING_SUCCESS
                            ? t("ROOSTER.PUBLIC-APP.SUCCESS_ANIMATION_ALT", {
                                defaultValue: "Meeting scheduled successfully"
                              })
                            : t("ROOSTER.PUBLIC-APP.ERROR_ANIMATION_ALT", {
                                defaultValue: "Meeting scheduling error"
                              })
                      }
                      data-testid="meeting-status-animation"
                    />
                  )}
                </Stack>
                <Stack
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ paddingBottom: "30px", minWidth: "100%" }}
                >
                  <Typography
                    component="h1"
                    fontSize={"23px"}
                    fontWeight={500}
                    textAlign="center"
                    width="100%"
                    aria-live="polite"
                    data-testid="meeting-status-heading"
                  >
                    {status === SCHEDULE_STATE.SCHEDULING &&
                      ui?.currentStep !== "loading" &&
                      t("ROOSTER.PUBLIC-APP.SETTING_UP_MEETING", {
                        defaultValue: "We are setting up your meeting"
                      })}
                    {status === SCHEDULE_STATE.SCHEDULING_SUCCESS &&
                      ui?.currentStep !== "loading" &&
                      (sessionData?.expiredLink
                        ? t("ROOSTER.PUBLIC-APP.MEETING_WAS_SCHEDULED", {
                            defaultValue: "This meeting was scheduled"
                          })
                        : t("ROOSTER.PUBLIC-APP.MEETING_IS_SCHEDULED", {
                            defaultValue: "This meeting is scheduled"
                          }))}
                    {status === SCHEDULE_STATE.SCHEDULING_FAIL &&
                      ui?.currentStep !== "loading" &&
                      t("ROOSTER.PUBLIC-APP.MEETING_NOT_SCHEDULED", {
                        defaultValue: "This meeting is not scheduled"
                      })}
                    {status === SCHEDULE_STATE.MEETING_CANCELLED &&
                      ui?.currentStep !== "loading" &&
                      t("ROOSTER.PUBLIC-APP.MEETING_CANCELLED", {
                        defaultValue: "Meeting cancelled"
                      })}
                  </Typography>

                  {ui?.currentStep === "loading" && (
                    <Skeleton variant="text" width="50%" height={55} />
                  )}
                  {status === SCHEDULE_STATE.SCHEDULING_SUCCESS &&
                    ui?.currentStep !== "loading" && (
                      <Stack alignItems="center" justifyContent="center">
                        <Typography
                          fontSize="15px"
                          textAlign="center"
                          color="text.secondary"
                          sx={{
                            px: "12px"
                          }}
                          data-testid="email-confirmation-text"
                        >
                          {t("ROOSTER.PUBLIC-APP.EMAIL_CALENDAR_INVITATION", {
                            defaultValue:
                              "We emailed you and the other participants a calendar invitation with all the details."
                          })}
                        </Typography>
                        {sessionData?.isRescheduleFlow &&
                          sessionData?.rescheduleReason?.reason?.reason && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              <strong>
                                {t("ROOSTER.PUBLIC-APP.REASON_LABEL", {
                                  defaultValue: "Reason:"
                                })}
                              </strong>{" "}
                              {sessionData.rescheduleReason.reason.reason}
                            </Typography>
                          )}
                        {sessionData?.isRescheduleFlow &&
                          sessionData?.rescheduleReason?.comment && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              <strong>
                                {t("ROOSTER.PUBLIC-APP.COMMENT_LABEL", {
                                  defaultValue: "Comment:"
                                })}
                              </strong>{" "}
                              {sessionData.rescheduleReason.comment}
                            </Typography>
                          )}
                      </Stack>
                    )}

                  {status === SCHEDULE_STATE.SCHEDULING_FAIL &&
                    ui?.currentStep !== "loading" && (
                      <Stack alignItems="center" justifyContent="center">
                        <Typography
                          fontSize="15px"
                          color="textSecondary"
                          textAlign="center"
                          sx={{
                            px: "12px"
                          }}
                        >
                          {ui?.meetingCardError ? (
                            <Alert
                              severity="error"
                              variant="standard"
                              sx={{ px: "10px", py: "1px", mb: 1 }}
                              data-testid="meeting-error-alert"
                            >
                              {ui?.meetingCardError?.message}
                            </Alert>
                          ) : (
                            t("ROOSTER.PUBLIC-APP.UNABLE_SCHEDULE_MEETING", {
                              defaultValue:
                                "We were unable to schedule your meeting. Please contact scheduler."
                            })
                          )}
                        </Typography>
                      </Stack>
                    )}

                  {status === SCHEDULE_STATE.MEETING_CANCELLED &&
                    ui?.currentStep !== "loading" && (
                      <Stack alignItems="center" justifyContent="center">
                        <Typography
                          fontSize="15px"
                          color="textSecondary"
                          textAlign="center"
                          sx={{
                            px: "12px"
                          }}
                        >
                          {meetingLink?.mode === "availability"
                            ? t(
                                "ROOSTER.PUBLIC-APP.INTERVIEW_CANCELLED_CONTACT_RECRUITER",
                                {
                                  defaultValue:
                                    "Your interview has been cancelled. Please contact your recruiter to reschedule."
                                }
                              )
                            : t(
                                "ROOSTER.PUBLIC-APP.MEETING_CANCELLED_PARTICIPANTS_NOTIFIED",
                                {
                                  defaultValue:
                                    "This meeting was cancelled. All the participants have been notified about it."
                                }
                              )}
                        </Typography>
                        {cancelledReason?.reason && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>
                              {t("ROOSTER.PUBLIC-APP.REASON_LABEL", {
                                defaultValue: "Reason:"
                              })}
                            </strong>{" "}
                            {cancelledReason.reason}
                          </Typography>
                        )}
                        {cancelledReason?.comment && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            <strong>
                              {t("ROOSTER.PUBLIC-APP.COMMENT_LABEL", {
                                defaultValue: "Comment:"
                              })}
                            </strong>{" "}
                            {cancelledReason.comment}
                          </Typography>
                        )}
                      </Stack>
                    )}

                  {ui?.currentStep === "loading" && (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                    >
                      <Skeleton variant="text" width="75%" height={22} />
                      <Skeleton variant="text" width="50%" height={22} />
                    </Stack>
                  )}

                  {meetingLink?.mode !== "availability" && (
                    <>
                      <Divider orientation="horizontal" flexItem />
                      <Stack
                        direction="column"
                        sx={{
                          minWidth: "100%",
                          px: { xs: 1, md: 5 },
                          pt: 2,
                          pb: 1
                        }}
                      >
                        <Stack spacing={2}>
                          {/* When section */}
                          <Stack
                            direction="row"
                            spacing={5}
                            data-testid="meeting-when-section"
                          >
                            <Stack minWidth="60px" maxWidth={"60px"}>
                              {ui?.currentStep === "loading" ? (
                                <Skeleton
                                  variant="text"
                                  width="41px"
                                  height="24px"
                                />
                              ) : (
                                <Typography
                                  fontSize="15px"
                                  fontWeight={500}
                                  textTransform="uppercase"
                                >
                                  {t("ROOSTER.PUBLIC-APP.WHEN", {
                                    defaultValue: "When"
                                  })}
                                </Typography>
                              )}
                            </Stack>

                            <Stack>
                              {ui?.currentStep === "loading" ? (
                                <Box width="100%">
                                  <Skeleton
                                    variant="text"
                                    width="75%"
                                    height="24px"
                                  />
                                  <Skeleton
                                    variant="text"
                                    width="60%"
                                    height="24px"
                                  />
                                </Box>
                              ) : (
                                <MeetingWhen
                                  prevSelectedTimeSlot={previouslySelectedTime}
                                  selectedTimeSlot={currentlySelectedSlot}
                                  previouslySelectedDuration={
                                    previouslySelectedDuration
                                  }
                                  timezone={sessionData?.selectedTimezone ?? ""}
                                  meetingDuration={
                                    meetingLink?.availability?.duration ?? 0
                                  }
                                  sessionData={sessionData}
                                  strikePrevSlot={
                                    status ===
                                      SCHEDULE_STATE.MEETING_CANCELLED ||
                                    !cleanSession ||
                                    status === SCHEDULE_STATE.SCHEDULING
                                  }
                                />
                              )}
                            </Stack>
                          </Stack>

                          {/* Who section */}
                          <Stack
                            direction="row"
                            spacing={5}
                            data-testid="meeting-who-section"
                          >
                            <Stack minWidth="60px">
                              {ui?.currentStep === "loading" ? (
                                <Skeleton
                                  variant="text"
                                  width="34px"
                                  height="24px"
                                />
                              ) : (
                                <Typography
                                  fontSize="15px"
                                  fontWeight={500}
                                  textTransform="uppercase"
                                >
                                  {t("ROOSTER.PUBLIC-APP.WHO", {
                                    defaultValue: "Who"
                                  })}
                                </Typography>
                              )}
                            </Stack>
                            <Stack>
                              <Stack
                                spacing={1}
                                width="100%"
                                overflow="auto"
                                sx={{
                                  maxHeight: "25vh",
                                  overflowY: "auto",
                                  overflowX: "auto",
                                  paddingRight: "8px !important",
                                  "&::-webkit-scrollbar": {
                                    width: "4px",
                                    background: "transparent",
                                    borderRadius: "10px"
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "transparent",
                                    backgroundClip: "padding-box",
                                    borderRadius: "10px"
                                  },
                                  "&:hover": {
                                    "&::-webkit-scrollbar-thumb": {
                                      backgroundColor: "#888",
                                      backgroundClip: "padding-box"
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                      backgroundColor: "#555"
                                    }
                                  }
                                }}
                              >
                                {ui?.currentStep === "loading" &&
                                  range(0, 2).map((v) => (
                                    <Stack
                                      direction="column"
                                      key={v}
                                      width="100%"
                                    >
                                      <Skeleton width="75%" height={20} />
                                      <Skeleton width="75%" height={20} />
                                    </Stack>
                                  ))}
                                {ui?.currentStep !== "loading" &&
                                  [
                                    {
                                      name: `${sessionData?.formData?.firstName} ${sessionData?.formData?.lastName}`,
                                      email: sessionData?.formData?.email ?? "",
                                      isCandidate: true
                                    },
                                    ...(!sessionData?.isRescheduleFlow &&
                                    meetingLink?.interviewersFilter
                                      ? (
                                          sessionData?.selectedSlot
                                            ?.participants ?? []
                                        ).map((p) => ({
                                          ...p,
                                          name: p?.name ?? "",
                                          email: p.value,
                                          isCandidate: false
                                        }))
                                      : (meetingLink?.interviewers ?? []))
                                  ]?.map(
                                    ({ name, email, isCandidate = false }) => {
                                      return (
                                        <Stack
                                          direction="row"
                                          key={email}
                                          id={email}
                                          data-testid={`meeting-participant-${email}`}
                                        >
                                          <Stack>
                                            <Tooltip
                                              title={name}
                                              disableHoverListener={true}
                                            >
                                              <Typography
                                                noWrap
                                                textOverflow="ellipsis"
                                                maxWidth="300px"
                                                fontSize="15px"
                                                fontWeight={500}
                                                overflow="hidden"
                                                textTransform="capitalize"
                                              >
                                                {ui?.currentStep ===
                                                "loading" ? (
                                                  <Skeleton
                                                    width={220}
                                                    height={20}
                                                  />
                                                ) : (
                                                  <>
                                                    {name && (
                                                      <>
                                                        {" "}
                                                        {name}{" "}
                                                        {name?.toLowerCase() ===
                                                          host?.name?.toLowerCase() && (
                                                          <Chip
                                                            label={
                                                              meetingLink?.type ===
                                                              "rooster_links"
                                                                ? t(
                                                                    "ROOSTER.PUBLIC-APP.HOST",
                                                                    {
                                                                      defaultValue:
                                                                        "Host"
                                                                    }
                                                                  )
                                                                : t(
                                                                    "ROOSTER.PUBLIC-APP.SCHEDULER",
                                                                    {
                                                                      defaultValue:
                                                                        "Scheduler"
                                                                    }
                                                                  )
                                                            }
                                                            variant="outlined"
                                                            size="small"
                                                          />
                                                        )}
                                                        {isCandidate && (
                                                          <Chip
                                                            label={t(
                                                              "ROOSTER.PUBLIC-APP.YOU",
                                                              {
                                                                defaultValue:
                                                                  "You"
                                                              }
                                                            )}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ ml: 0.5 }}
                                                          />
                                                        )}
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                              </Typography>
                                            </Tooltip>
                                          </Stack>
                                        </Stack>
                                      );
                                    }
                                  )}
                              </Stack>
                            </Stack>
                          </Stack>

                          {/* Where section */}
                          <Stack
                            direction="row"
                            spacing={5}
                            data-testid="meeting-where-section"
                          >
                            {status !== SCHEDULE_STATE.MEETING_CANCELLED && (
                              <Stack minWidth="60px">
                                {ui?.currentStep === "loading" ? (
                                  <Skeleton
                                    variant="text"
                                    width="48px"
                                    height="24px"
                                  />
                                ) : (
                                  <Typography
                                    fontSize="15px"
                                    fontWeight={500}
                                    textTransform="uppercase"
                                  >
                                    {t("ROOSTER.PUBLIC-APP.WHERE", {
                                      defaultValue: "Where"
                                    })}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                            {status !== SCHEDULE_STATE.MEETING_CANCELLED && (
                              <Stack>
                                {ui?.currentStep !== "loading" && (
                                  <Stack width="100%">
                                    <Typography
                                      fontSize="15px"
                                      fontWeight={500}
                                    >
                                      {t(
                                        "ROOSTER.PUBLIC-APP.MEETING_DETAILS_CONFIRMATION_EMAIL",
                                        {
                                          defaultValue:
                                            "Meeting details to follow in the confirmation email"
                                        }
                                      )}
                                    </Typography>
                                  </Stack>
                                )}
                                {ui?.currentStep === "loading" && (
                                  <Box minWidth="100%">
                                    <Skeleton
                                      variant="text"
                                      width="100%"
                                      height={20}
                                    />
                                    <Skeleton
                                      variant="text"
                                      width="25%"
                                      height={20}
                                    />
                                  </Box>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>

                {status === SCHEDULE_STATE.SCHEDULING_SUCCESS &&
                  ui?.currentStep !== "loading" && (
                    <>
                      {(!meetingLink?.canCancel ||
                        !meetingLink?.canReschedule) && (
                        <Alert
                          severity="warning"
                          variant="outlined"
                          sx={{
                            width: "85%",
                            px: "10px",
                            py: "0px",
                            mt: "-20px",
                            mb: "25px"
                          }}
                          data-testid="meeting-reschedule-cancel-alert"
                        >
                          {isRoosterLink
                            ? roosterLinkAvailabilityMessage
                            : autobookCancelOrRescheduleAlertMessage}
                        </Alert>
                      )}

                      {sessionData?.computedAvailability?.length === 0 &&
                        !loadingAvailability &&
                        sessionData?.isRescheduleFlow === true && (
                          <Alert
                            severity="warning"
                            variant="outlined"
                            sx={{
                              width: "85%",
                              px: "10px",
                              py: "0px",
                              mt: "-20px",
                              mb: "25px"
                            }}
                            data-testid="meeting-no-availability-alert"
                          >
                            {t(
                              "ROOSTER.PUBLIC-APP.NO_AVAILABILITY_CANNOT_RESCHEDULE",
                              {
                                defaultValue:
                                  "No alternative time slots are currently available for rescheduling."
                              }
                            )}
                          </Alert>
                        )}

                      {!sessionData?.expiredLink && (
                        <>
                          <Divider orientation="horizontal" flexItem />
                          <Stack
                            direction="row"
                            flex={1}
                            spacing={2}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ padding: "10px 10px" }}
                            data-testid="meeting-actions-row"
                          >
                            <Typography
                              fontSize="15px"
                              color="text.secondary"
                              data-testid="need-change-text"
                            >
                              {t("ROOSTER.PUBLIC-APP.NEED_MAKE_CHANGE", {
                                defaultValue: "Need to make a change ?"
                              })}
                            </Typography>
                            <Stack direction="row" spacing={3}>
                              {!isCancelling && (
                                <Button
                                  size="small"
                                  sx={{
                                    px: "10px",
                                    textTransform: "capitalize",
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                  onClick={() => handleRescheduleClick()}
                                  data-testid="reschedule-button"
                                  disabled={
                                    loadingAvailability ||
                                    !meetingLink?.canReschedule ||
                                    isPreparingSchedulingState ||
                                    isCancelling ||
                                    !autobookStatus
                                  }
                                >
                                  {(isPreparingSchedulingState ||
                                    loadingAvailability) && (
                                    <CircularProgress
                                      size={15}
                                      thickness={6}
                                      sx={{ mr: "10px" }}
                                      aria-label={t(
                                        "ROOSTER.PUBLIC-APP.LOADING",
                                        {
                                          defaultValue: "Loading"
                                        }
                                      )}
                                    />
                                  )}
                                  {isPreparingSchedulingState ||
                                  loadingAvailability
                                    ? t("ROOSTER.PUBLIC-APP.CHECKING", {
                                        defaultValue: "Checking Availability"
                                      })
                                    : t("ROOSTER.PUBLIC-APP.RESCHEDULE", {
                                        defaultValue: "Reschedule"
                                      })}
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                sx={{
                                  px: "10px",
                                  textTransform: "capitalize"
                                }}
                                data-testid="cancel-meeting-button"
                                disabled={
                                  !meetingLink?.canCancel ||
                                  isCancelling ||
                                  isPreparingSchedulingState ||
                                  !autobookStatus
                                }
                                onClick={() => setShowCancelDialogState(true)}
                              >
                                {isCancelling
                                  ? t("ROOSTER.PUBLIC-APP.CANCELLING", {
                                      defaultValue: "Cancelling"
                                    })
                                  : t("ROOSTER.PUBLIC-APP.CANCEL", {
                                      defaultValue: "Cancel"
                                    })}
                              </Button>
                            </Stack>
                          </Stack>
                        </>
                      )}
                    </>
                  )}

                {status === SCHEDULE_STATE.SCHEDULING_FAIL && (
                  <>
                    <Divider orientation="horizontal" flexItem />{" "}
                    <Stack
                      direction="row"
                      flex={1}
                      spacing={3}
                      alignItems="center"
                      sx={{ padding: "10px", textTransform: "capitalize" }}
                    >
                      <Typography
                        variant="subtitle2"
                        component="p"
                        data-testid="retry-prompt"
                      >
                        {t("ROOSTER.PUBLIC-APP.WANT_TO_RETRY", {
                          defaultValue: "Do you want to retry ?"
                        })}
                      </Typography>
                      <span>
                        <Button
                          size="small"
                          onClick={() => {
                            handleScheduleAgain();
                          }}
                          data-testid="schedule-again-button"
                        >
                          {t("ROOSTER.PUBLIC-APP.SCHEDULE_AGAIN", {
                            defaultValue: "Schedule again"
                          })}
                        </Button>
                        &nbsp;
                      </span>
                    </Stack>
                  </>
                )}
              </Stack>
            </Card>
          </Stack>
          <AltChatUI
            view="meetingConfirmationView"
            showChatIcon={isSmallScreen || isMediumScreen}
          />
        </Stack>
      </Stack>
    </Grow>
  );
};
